/**
 * Types for AI validation reasoning and field discrepancy tracking
 */

/**
 * Status of a validated field
 */
export type FieldValidationStatus =
  | "suggested_add" // AI suggests adding this value to the system (blue icon)
  | "suggested_update" // AI suggests updating the system value (blue icon)
  | "suggested_review" // AI suggests reviewing this field (blue icon)
  | "auto_corrected" // AI auto-corrected with high confidence (green icon)
  | "flagged" // Discrepancy found, but not critical (yellow icon)
  | "requires_review" // Manual review required (red icon)
  | "validated"; // Successfully validated, no issues (no icon or subtle checkmark)

/**
 * Suggested action type for corrections
 */
export type SuggestedAction = "add" | "update" | "review";

/**
 * Severity level for corrections
 */
export type CorrectionSeverity = "minor" | "moderate" | "major";

/**
 * A suggested correction from the validator agent
 */
export interface SuggestedCorrection {
  /** Name of the field to correct */
  field_name: string;
  /** Value extracted from document */
  extracted_value: string | null;
  /** Current value in system/database */
  existing_value: string | null;
  /** Action to take: 'add', 'update', or 'review' */
  suggested_action: SuggestedAction;
  /** Severity level: 'minor', 'moderate', or 'major' */
  severity: CorrectionSeverity;
  /** AI reasoning for suggesting this correction */
  reasoning: string;
}

/**
 * Carrier data for interline shipments
 */
export interface CarrierData {
  id: number;
  name: string | null;
  ref_carrier_code: string | null;
  ref_scac_id: string | null;
}

/**
 * Driver data from pickup/delivery events
 */
export interface DriverData {
  name: string | null;
  ref_driver_code: string | null;
}

/**
 * Full matched shipment data from the system database
 */
export interface MatchedShipmentData {
  found?: boolean;
  shipment_id: number;
  pro_number: string | null;
  pickup_date: string | null;
  due_service_date: string | null;
  due_service_timestamp_utc: string | null;
  weight: number | null;
  pieces: number | null;
  pallets: number | null;
  shipment_status: string | null;
  shipper: ShippingEntityData | null;
  consignee: ShippingEntityData | null;
  bill_to: ShippingEntityData | null;
  origin_sic: ServiceCenterData | null;
  destination_sic: ServiceCenterData | null;
  shipment_details: ShipmentDetailData[];
  traces: TraceData[];
  tags: TagData[];
  notes: NoteData[];
  instructions: InstructionData[];
  accessorials: AccessorialData[];
  /** Rollup charges total */
  rollup_charges: number | null;
  /** True if billing has been completed (BDNE status exists) */
  billing_completed: boolean;
  // === NEW FIELDS for Pipeline Cost Optimization ===
  /** Service level code (STD, EXP, GRD, etc.) */
  service_level: string | null;
  /** Customer reference code from bill_to entity */
  customer_ref_code: string | null;
  /** Origin interline carrier (if orig_interliner_id is set) */
  origin_carrier: CarrierData | null;
  /** Destination interline carrier (if dest_interliner_id is set) */
  destination_carrier: CarrierData | null;
  /** Pickup driver info from PCKD status event */
  pickup_driver: DriverData | null;
  /** Delivery driver info from DLVR status event */
  delivery_driver: DriverData | null;
  /** Actual pickup time in UTC */
  actual_pickup_time_utc: string | null;
  /** Actual pickup time in local timezone */
  actual_pickup_time_local: string | null;
  /** Actual delivery time in UTC */
  actual_delivery_time_utc: string | null;
  /** Actual delivery time in local timezone */
  actual_delivery_time_local: string | null;
}

export interface ShippingEntityData {
  id: number;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  ref_client_code: string | null;
}

export interface ServiceCenterData {
  id?: number;
  code: string;
  name: string | null;
  city: string | null;
  state: string | null;
  /** @deprecated Not returned from API - use lookup if needed */
  zip?: string | null;
  /** @deprecated Not returned from get_full_shipment_data - use separate lookup */
  time_zone?: string | null;
}

export interface ShipmentDetailData {
  id?: number;
  seq_number: number | null;
  description: string | null;
  pieces: number | null;
  pieces_unit: string | null;
  pallets: number | null;
  pallets_unit: string | null;
  weight: number | null;
  weight_unit: string | null;
  said_to_contain: string | null;
  commodity_code: string | null;
  actual_class: string | null;
  /** @deprecated Use commodity_code instead */
  nmfc_number?: string | null;
  /** @deprecated Use actual_class instead */
  freight_class?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dim_unit?: string | null;
}

export interface TraceData {
  id?: number;
  type_id: number | null;
  type_code: string;
  type_description: string | null;
  value: string;
}

export interface TagData {
  id?: number;
  name: string;
  description: string | null;
  color_hex: string | null;
}

export interface NoteData {
  id?: number;
  type_id: number | null;
  type_name: string | null;
  text: string | null;
  created_on: string | null;
}

export interface InstructionData {
  id?: number;
  code_id: number | null;
  code: string;
  description: string | null;
}

export interface AccessorialData {
  id?: number;
  code: string;
  description: string | null;
  qty: number | null;
  rate: number | null;
  charge: number | null;
}

/**
 * Validation metadata from validator agent
 */
export interface ValidationMetadata {
  /** Summary of validation findings */
  summary: string | null;
  /** Validator recommendation: 'APPROVE', 'REVIEW', etc. */
  recommendation: string | null;
  /** Whether manual review is required */
  requires_manual_review: boolean;
  /** Results of specific validation checks */
  validation_checks: Record<string, boolean>;
  /** List of suggested corrections from validator */
  suggested_corrections: SuggestedCorrection[];
  /** Full matched shipment data from system database */
  matched_shipment_data: MatchedShipmentData | null;
  /** Additional metadata from validation */
  metadata: Record<string, unknown>;
}

/**
 * Data sources used for extraction and validation
 */
export type DataSource =
  | "ocr" // Text extracted via OCR
  | "vision" // Visual analysis (e.g., Claude Vision API)
  | "shipment_db" // Shipment database lookup
  | "customer_history" // Historical customer data and patterns
  | "rate_table" // Rate confirmation data
  | "entity_lookup"; // Shipping entity/customer lookup

/**
 * Discrepancy between extracted value and reference data
 */
export interface FieldDiscrepancy {
  /** Source system that has conflicting data */
  source: DataSource;

  /** Conflicting value from the source (null if source has no value) */
  value: string | number | null;

  /** Explanation of why values differ */
  reason?: string;
}

/**
 * Historical customer correction patterns
 */
export interface CustomerPattern {
  /** Description of the pattern */
  description: string;

  /** Number of times this pattern has been observed */
  occurrenceCount: number;

  /** Confidence in this pattern (0-100) */
  confidence: number;
}

/**
 * Validation metadata for an extracted field
 */
export interface FieldValidation {
  /** Current validation status */
  status: FieldValidationStatus;

  /** AI reasoning for the extracted value and validation decision */
  reasoning?: string;

  /** For suggested corrections: the action to take */
  suggested_action?: SuggestedAction;

  /** For suggested corrections: severity level */
  severity?: CorrectionSeverity;

  /** For suggested corrections: value extracted from document */
  extracted_value?: string | null;

  /** For suggested corrections: current value in system */
  existing_value?: string | null;

  /** For auto-corrections: original value before correction */
  original_value?: string | null;

  /** For auto-corrections: corrected value */
  corrected_value?: string | null;

  /** Data sources used for this field's extraction */
  data_sources: DataSource[];

  /** Discrepancies found during validation */
  discrepancies: FieldDiscrepancy[];

  /** Customer-specific correction patterns, if applicable */
  customer_patterns?: string;
}

/**
 * Complete extracted field data with validation metadata
 */
export interface ExtractedFieldData {
  /** The extracted value - can be primitive, array, or object */
  value: string | number | boolean | null | unknown[] | Record<string, unknown>;

  /** AI confidence score (0-1) */
  confidence: number;

  /** OCR bounding box coordinates (optional) */
  bounding_box?: {
    x: number;
    y: number;
    width: number;
    height: number;
    page?: number;
  };

  /** Validation metadata */
  validation?: FieldValidation;
}

/**
 * Helper function to get icon type based on validation status
 */
export function getValidationIconType(
  status: FieldValidationStatus
): "success" | "warning" | "error" | "info" {
  switch (status) {
    case "auto_corrected":
      return "success";
    case "suggested_add":
    case "suggested_update":
    case "suggested_review":
      return "info";
    case "flagged":
      return "warning";
    case "requires_review":
      return "error";
    case "validated":
      return "info";
    default:
      return "info";
  }
}

/**
 * Helper function to get color class based on validation status
 */
export function getValidationStatusColor(status: FieldValidationStatus): {
  icon: string;
  bg: string;
  text: string;
  border: string;
} {
  switch (status) {
    case "auto_corrected":
      return {
        icon: "text-green-600 dark:text-green-400",
        bg: "bg-green-50 dark:bg-green-900/20",
        text: "text-green-700 dark:text-green-300",
        border: "border-green-200 dark:border-green-800",
      };
    case "suggested_add":
    case "suggested_update":
    case "suggested_review":
      return {
        icon: "text-blue-600 dark:text-blue-400",
        bg: "bg-blue-50 dark:bg-blue-900/20",
        text: "text-blue-700 dark:text-blue-300",
        border: "border-blue-200 dark:border-blue-800",
      };
    case "flagged":
      return {
        icon: "text-yellow-600 dark:text-yellow-400",
        bg: "bg-yellow-50 dark:bg-yellow-900/20",
        text: "text-yellow-700 dark:text-yellow-300",
        border: "border-yellow-200 dark:border-yellow-800",
      };
    case "requires_review":
      return {
        icon: "text-red-600 dark:text-red-400",
        bg: "bg-red-50 dark:bg-red-900/20",
        text: "text-red-700 dark:text-red-300",
        border: "border-red-200 dark:border-red-800",
      };
    case "validated":
      return {
        icon: "text-blue-600 dark:text-blue-400",
        bg: "bg-blue-50 dark:bg-blue-900/20",
        text: "text-blue-700 dark:text-blue-300",
        border: "border-blue-200 dark:border-blue-800",
      };
    default:
      return {
        icon: "text-slate-600 dark:text-slate-400",
        bg: "bg-slate-50 dark:bg-slate-900/20",
        text: "text-slate-700 dark:text-slate-300",
        border: "border-slate-200 dark:border-slate-800",
      };
  }
}

/**
 * Format data source for display
 */
export function formatDataSource(source: DataSource): string {
  const labels: Record<DataSource, string> = {
    ocr: "OCR",
    vision: "Vision AI",
    shipment_db: "Shipment Database",
    customer_history: "Customer History",
    rate_table: "Rate Table",
    entity_lookup: "Entity Lookup",
  };
  return labels[source] || source;
}

/**
 * Get validation status label for display
 */
export function getValidationStatusLabel(status: FieldValidationStatus): string {
  const labels: Record<FieldValidationStatus, string> = {
    suggested_add: "Suggested: Add to System",
    suggested_update: "Suggested: Update Value",
    suggested_review: "Suggested: Review",
    auto_corrected: "Auto-Corrected",
    flagged: "Flagged for Attention",
    requires_review: "Requires Review",
    validated: "Validated",
  };
  return labels[status] || status;
}

/**
 * Get severity color classes
 */
export function getSeverityColor(severity: CorrectionSeverity): {
  bg: string;
  text: string;
  border: string;
  badge: string;
} {
  switch (severity) {
    case "major":
      return {
        bg: "bg-red-50 dark:bg-red-900/20",
        text: "text-red-700 dark:text-red-300",
        border: "border-red-300 dark:border-red-800",
        badge: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
      };
    case "moderate":
      return {
        bg: "bg-yellow-50 dark:bg-yellow-900/20",
        text: "text-yellow-700 dark:text-yellow-300",
        border: "border-yellow-300 dark:border-yellow-800",
        badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
      };
    case "minor":
    default:
      return {
        bg: "bg-blue-50 dark:bg-blue-900/20",
        text: "text-blue-700 dark:text-blue-300",
        border: "border-blue-300 dark:border-blue-800",
        badge: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      };
  }
}

/**
 * Get action label for display
 */
export function getActionLabel(action: SuggestedAction): string {
  const labels: Record<SuggestedAction, string> = {
    add: "Add to System",
    update: "Update Value",
    review: "Review Required",
  };
  return labels[action] || action;
}

/**
 * Format field name for display (convert snake_case to Title Case)
 */
export function formatFieldNameForDisplay(fieldName: string): string {
  return fieldName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
